import { ViewChild, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IComplimentaryComplianceReport,
} from '@module/complimentary-compliance-report/complimentary-compliance-report.types';
import {
  ComplimentaryComplianceReportService,
} from '@module/complimentary-compliance-report/complimentary-compliance-report.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { IApp } from '@core/models/app.interfaces';
import IDocument = IApp.IDocument;
import { environment } from '@env/environment';
import { UploadDocumentService } from '@shared/services/upload-document.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ServiceMessageComponent } from '@core/components/service-message/service-message.component';
import { DEFAULT_DIALOG_CONFIG, SereviceMessageType } from '@core/constants/serviceMessage.const';
import { ModalService } from '@shared/services/modal.service';
import { Dialog, DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@shared/components/ng-select/ng-select.module';
import { DocumentsComponent } from '@shared/components/documents/documents.component';
import { DisableOnUploadDirective } from '@shared/directives/disable-on-upload.directive';
import { WCAGLabelDirective } from '@shared/directives/wcag-label.directive';

@Component({
  selector: 'app-complimentary-compliance-add-details-form',
  templateUrl: './complimentary-compliance-add-details-form.component.html',
  styleUrls: ['./complimentary-compliance-add-details-form.component.scss'],
  imports: [TranslateModule, FormlyModule, DialogModule, ReactiveFormsModule, CommonModule,
    TooltipModule, FontAwesomeModule, FormsModule, NgSelectModule, DocumentsComponent, DisableOnUploadDirective,
    WCAGLabelDirective
  ]
})
export class ComplimentaryComplianceAddDetailsFormComponent implements OnInit, OnDestroy {

  @ViewChild('dialog', { static: false }) dialog: Dialog;

  @Input() form = new FormGroup({});
  @Input() masterProject: any;
  @Output() modelIdUpdate = new EventEmitter();

  public report: IComplimentaryComplianceReport;
  public model: any = {};
  public fields: FormlyFieldConfig[];
  public needMoreInfoFields: FormlyFieldConfig[];
  public documents: IDocument[];
  public isDocumentUploadInProgress = false;

  public visible = true;
  private _destroy$ = new Subject<any>();

  public currentStepObj;
  public downloadSelected;
  public taskList = [];
  public documentUploadDisabled = false;
  missingDocsCode: string[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<ComplimentaryComplianceAddDetailsFormComponent>,
    private service: ComplimentaryComplianceReportService,
    private uploadDocumentService: UploadDocumentService,
    private translateService: TranslateService,
    private modalService: ModalService,
  ) { }

  ngOnInit() {
    this.currentStepObj = this.data.currentStepObj;
    this.currentStepObj.onBeforeAddDetails();
    this.data.section && (this.currentStepObj.section = this.data.section);
    this.currentStepObj.dialogRef = this.dialogRef;
    this.setTaskList();
  }

  scrollToTop(): void {
    setTimeout(() => {
      this.dialog.el.nativeElement.querySelector('.p-dialog-content').scrollTo(0, 0);
    }, 500);
  }

  public save() {
    if (!this.requiredNestedFields) {
      return;
    }
    if (this.currentStepObj.detailSectionDocumentUploadConfig &&
        !this.uploadDocumentService.checkMandatoryDocuments(
          this.missingDocsCode,
          this.currentStepObj.detailSectionDocumentUploadConfig,
          this.currentStepObj.optionalDocsWithPlaceholderCodes
        )) {
        return;
    }
    this.currentStepObj.save(this.dialogRef);
  }

  public close(): void {
    if (this.currentStepObj.form.touched) {
      const sub$ = this.service.openConfirmationModal('confirmOnClose').subscribe(result => {
        sub$.unsubscribe();
        if (result) {
          this.dialogRef.close(false);
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  documentUploadHandler(documentResult: IApp.IEmmitedDocs): void {
    this.documents = documentResult.docs;
    this.missingDocsCode = documentResult.missingDocsCode;
    this.isDocumentUploadInProgress = documentResult.docs
      .filter(document => document._uploadStatus)
      .some(document => document._uploadStatus !== 'CLEAN');
  }

  private setTaskList() {
    if (this._hasDocumentConfigs() && this.service.currentStep.code === 'STEP-4'){
          this.taskList = this.currentStepObj.detailSectionDocumentUploadConfig.documentUploadConfigs.map(entity => {
            return {
              id: entity.documentTypeCode,
              name: this.translateService.instant(`COMMON.documentList.${ entity.documentTypeCode}`)
            }
          });
        }
  }

  private get requiredNestedFields(): boolean {
    const missingNestedFields = this.currentStepObj.validateRequiredNestedFields;
    if (Object.keys(missingNestedFields).length) {
      this.nestedFieldsRequired(missingNestedFields);
      return false;
    } else {
      return true;
    }
  }

  private nestedFieldsRequired(missingFields) {
    let msg = '';
    Object.keys(missingFields).forEach(key => msg += `<li>${ this.translateService.instant(missingFields[key]) }</li>`);
    const metaData = [`<ol type="a">${ msg }</ol>`];
    this.modalService.open(
      ServiceMessageComponent,
      {
        messages: [
          {
            message: 'mandatoryNestedFieldsRequired',
            metaData,
          },
        ],
        type: SereviceMessageType.ERROR,
      },
      true,
      DEFAULT_DIALOG_CONFIG,
    );
  }

  ngOnDestroy() {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  private _hasDocumentConfigs() {
    return this.currentStepObj.detailSectionDocumentUploadConfig &&
      this.currentStepObj.detailSectionDocumentUploadConfig.documentUploadConfigs;
  }

}
